import rawSleeperPlayers from "../data/sleeperPlayers.json";
import {
  playerGameLogsById,
  type PlayerGameLogPosition,
  type PlayerGameLogRow,
  type PlayerSeasonGameLogs,
} from "../data/playerGameLogs";

type SleeperPlayer = {
  id?: string;
  position?: string;
};

type SleeperPlayersPayload = {
  players?: SleeperPlayer[];
};

const PROFILE_POSITIONS = new Set<PlayerGameLogPosition>(["QB", "RB", "WR", "TE"]);

const sleeperPlayers = (((rawSleeperPlayers as unknown as SleeperPlayersPayload) ?? {}).players ?? []).filter(Boolean);

const positionByPlayerId = new Map<string, PlayerGameLogPosition>();

sleeperPlayers.forEach((player) => {
  const position = player.position;
  if (!player.id || !position || !PROFILE_POSITIONS.has(position as PlayerGameLogPosition)) return;
  positionByPlayerId.set(player.id, position as PlayerGameLogPosition);
});

function rowHasRelevantData(row: PlayerGameLogRow) {
  return Object.entries(row).some(([key, value]) => {
    if (key === "week" || key === "opponent" || key === "note") return false;
    return typeof value === "number" && Number.isFinite(value) && value > 0;
  });
}

const gameRowsCache = new Map<string, PlayerGameLogRow[]>();
const seasonLogsCache = new Map<string, PlayerSeasonGameLogs[]>();

export function getComparableGameRows(
  position: PlayerGameLogPosition | null | undefined,
  season: number
): PlayerGameLogRow[] {
  if (!position) return [];

  const cacheKey = `${position}:${season}`;
  const cached = gameRowsCache.get(cacheKey);
  if (cached) return cached;

  const rows: PlayerGameLogRow[] = [];

  Object.entries(playerGameLogsById).forEach(([playerId, seasons]) => {
    if (positionByPlayerId.get(playerId) !== position) return;
    const matchingSeason = seasons.find((entry) => entry.season === season);
    if (!matchingSeason) return;
    matchingSeason.rows.forEach((row) => {
      if (row.week > 0 && rowHasRelevantData(row)) {
        rows.push(row);
      }
    });
  });

  gameRowsCache.set(cacheKey, rows);
  return rows;
}

export function getComparableSeasonLogs(
  position: PlayerGameLogPosition | null | undefined,
  season: number
): PlayerSeasonGameLogs[] {
  if (!position) return [];

  const cacheKey = `${position}:${season}`;
  const cached = seasonLogsCache.get(cacheKey);
  if (cached) return cached;

  const seasons: PlayerSeasonGameLogs[] = [];

  Object.entries(playerGameLogsById).forEach(([playerId, playerSeasons]) => {
    if (positionByPlayerId.get(playerId) !== position) return;
    const matchingSeason = playerSeasons.find((entry) => entry.season === season);
    if (!matchingSeason) return;
    const rows = matchingSeason.rows.filter((row) => row.week > 0 && rowHasRelevantData(row));
    if (!rows.length) return;
    seasons.push({
      season: matchingSeason.season,
      rows,
    });
  });

  seasonLogsCache.set(cacheKey, seasons);
  return seasons;
}
